// components/VoipWidget.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Avatar, Badge, Button, Space, Tag, Descriptions,
  Drawer, Tabs, notification as antNotification, Spin, Input, Statistic, Row, Col, Progress
} from 'antd';
import {
  PhoneOutlined, CustomerServiceOutlined, ShoppingCartOutlined,
  UserOutlined, ClockCircleOutlined, EnvironmentOutlined,
  WalletOutlined, HistoryOutlined, LoadingOutlined, SearchOutlined,
  ShoppingOutlined, DatabaseOutlined, RiseOutlined, FallOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Api from '../../Network';
import { NameByLang } from '../NameByLang/NameByLang';
import './VoipWidget.css';

interface CallRecord {
  id: string;
  name: string;
  clientId: string;
  phone: string;
  customer: any;
  timestamp: Date;
}

const VOIP_SNAPSHOT_KEY = 'voip_widget_snapshot_v1';
const VOIP_SNAPSHOT_TTL_MS = 15 * 60 * 1000;
const VOIP_POSITION_KEY = 'voip_button_position_v1';

export const VoipWidget: React.FC = () => {
  const { company, userInfo } = useSelector((state: any) => state.setReducer || {});
  const locale = localStorage.getItem('i18nextLng') === 'ar' ? 'ar' : 'en';
  const currentUserVoipId = String(
    userInfo?.voip_id ??
    userInfo?.voipId ??
    userInfo?.ext_id ??
    ''
  ).trim();
  const currency =
    company?.company_information?.[`currency_${locale}`] ||
    company?.[`currency_${locale}`] ||
    'EGP';

  const [currentCall, setCurrentCall]     = useState<CallRecord | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isRinging, setIsRinging]         = useState(false);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [isSearching, setIsSearching]     = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [callStatus, setCallStatus]       = useState<'idle' | 'ringing' | 'active' | 'ended'>('idle');
  const [callDuration, setCallDuration]   = useState(0);
  const callTimerRef = useRef<number | undefined>(undefined);

  // ─── Drag state ───────────────────────────────────────────────────────────
  const voipRef   = useRef<HTMLDivElement>(null);
  const [position, setPosition]   = useState<{ x: number; y: number } | null>(null);
  const posRef    = useRef<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0, hasDragged: false });

  // Always-fresh ref — the event listener reads this instead of stale state
  const currentCallRef = useRef<CallRecord | null>(null);

  // Wrapper so both ref and state stay in sync
  const updateCall = useCallback((next: CallRecord | null) => {
    currentCallRef.current = next;
    setCurrentCall(next);
  }, []);

  const debounceRef = useRef<number | undefined>(undefined);
  const history     = useHistory();

  // ─── Drag helpers ─────────────────────────────────────────────────────────
  const clampToViewport = useCallback((x: number, y: number) => {
    const el = voipRef.current;
    const W  = window.innerWidth;
    const H  = window.innerHeight;
    const bW = el ? el.offsetWidth  : 66;
    const bH = el ? el.offsetHeight : 66;
    return {
      x: Math.max(8, Math.min(W - bW - 8, x)),
      y: Math.max(8, Math.min(H - bH - 8, y)),
    };
  }, []);

  const savePosition = useCallback((x: number, y: number) => {
    try { localStorage.setItem(VOIP_POSITION_KEY, JSON.stringify({ x, y })); } catch { /* ignore */ }
  }, []);

  // Restore saved position on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(VOIP_POSITION_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        const clamped = clampToViewport(parsed.x, parsed.y);
        posRef.current = clamped;
        setPosition(clamped);
      }
    } catch { /* ignore */ }
  }, [clampToViewport]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = voipRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = { active: true, startX: e.clientX, startY: e.clientY, originX: rect.left, originY: rect.top, hasDragged: false };
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const rect  = voipRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = { active: true, startX: touch.clientX, startY: touch.clientY, originX: rect.left, originY: rect.top, hasDragged: false };
  }, []);

  useEffect(() => {
    const onMove = (clientX: number, clientY: number) => {
      if (!dragState.current.active) return;
      const dx = clientX - dragState.current.startX;
      const dy = clientY - dragState.current.startY;
      if (!dragState.current.hasDragged && Math.abs(dx) + Math.abs(dy) > 4) {
        dragState.current.hasDragged = true;
        setIsDragging(true);
      }
      if (!dragState.current.hasDragged) return;
      const clamped = clampToViewport(dragState.current.originX + dx, dragState.current.originY + dy);
      posRef.current = clamped;
      setPosition(clamped);
    };

    const onEnd = () => {
      if (!dragState.current.active) return;
      dragState.current.active = false;
      setIsDragging(false);
      if (dragState.current.hasDragged && posRef.current) {
        savePosition(posRef.current.x, posRef.current.y);
      }
    };

    const onMouseMove = (e: MouseEvent)  => onMove(e.clientX, e.clientY);
    const onTouchMove = (e: TouchEvent)  => { e.preventDefault(); onMove(e.touches[0].clientX, e.touches[0].clientY); };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onEnd);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend',  onEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend',  onEnd);
    };
  }, [clampToViewport, savePosition]);

  const handleButtonClick = useCallback(() => {
    if (dragState.current.hasDragged) return;
    setDrawerVisible(true);
  }, []);

  const clearSnapshot = useCallback(() => {
    localStorage.removeItem(VOIP_SNAPSHOT_KEY);
  }, []);

  // The call's customer (fetched via customers/{id}/view) now carries its
  // segment inline, so we read the theme color straight from it — no need to
  // fetch the full segments list.
  const segmentColor =
    currentCall?.customer?.segment?.color_theme ||
    currentCall?.customer?.segment?.color ||
    '#1890ff';

  // Get basket data from backend response
  const getBasketData = useCallback(() => {
    const basketData = currentCall?.customer?.basket_data;
    
    if (!basketData) {
      return {
        averageBasketSize: 0,
        basketCount: 0,
        basketTotal: 0,
        completedBuckets: [] as Array<{ key: string; count: number; total: number; is_completed: boolean }>,
      };
    }

    // Expected shape:
    // {
    //   "Pending": { count, total, is_completed: false },
    //   "Order Placed": { count, total, is_completed: true }
    // }
    const buckets = Object.entries(basketData)
      .filter(([_, value]) => value && typeof value === 'object')
      .map(([key, value]: [string, any]) => ({
        key,
        count: Number(value?.count || 0),
        total: Number(value?.total || 0),
        is_completed: Boolean(value?.is_completed),
      }));

    const completedBuckets = buckets.filter((bucket) => bucket.is_completed);
    const basketCount = completedBuckets.reduce((sum, bucket) => sum + bucket.count, 0);
    const basketTotal = completedBuckets.reduce((sum, bucket) => sum + bucket.total, 0);
    const averageBasketSize = basketCount > 0 ? basketTotal / basketCount : 0;

    return { averageBasketSize, basketCount, basketTotal, completedBuckets };
  }, [currentCall?.customer?.basket_data]);

  // Calculate total orders count from orders array
  const getTotalOrdersCount = useCallback(() => {
    const orders = currentCall?.customer?.orders;
    return orders?.length || 0;
  }, [currentCall?.customer?.orders]);

  // ─── Manual in-widget search (fallback when listener finds nothing) ────────
  const manualSearch = useCallback(async (keyword: string) => {
    if (keyword.trim().length < 2) return;
    setIsSearching(true);

    try {
      // Step 1 — search
      const searchRes = await Api.post('customers/search', { keyword });
      let results: any[] = [];
      if (Array.isArray(searchRes.data))           results = searchRes.data;
      else if (Array.isArray(searchRes.data?.data)) results = searchRes.data.data;
      else if (Array.isArray(searchRes))            results = searchRes as any[];

      const match =
        results.find((c: any) => c.phone === keyword || c.mobile === keyword) ??
        results[0] ??
        null;

      if (!match) {
        antNotification.warning({
          message: 'Customer Not Found',
          description: `No registered customer with number: ${keyword}`,
          duration: 3,
          placement: 'topRight',
        });
        return;
      }

      // Step 2 — full details (Api returns data directly, no .data wrapper)
      const detailRes = await Api.get(`customers/${match.id}/view`);
      let customerData: any = null;

      // Api returns response directly (not response.data)
      const raw = detailRes?.data ?? detailRes;

      if (raw?.customer) {
        customerData = {
          ...raw.customer,
          orders:                raw.orders                ?? [],
          addresses:             raw.addresses             ?? [],
          activities:            raw.activities            ?? [],
          most_ordered_products: raw.most_ordered_products ?? [],
          basket_data:           raw.basket_data           ?? null,
        };
      } else if (raw?.id) {
        customerData = { orders: [], addresses: [], ...raw };
      }

      if (customerData && currentCallRef.current) {
        const updated: CallRecord = {
          ...currentCallRef.current,
          name:     customerData.name                               ?? currentCallRef.current.name,
          clientId: String(customerData.id ?? currentCallRef.current.clientId),
          phone:    customerData.phone ?? customerData.mobile       ?? currentCallRef.current.phone,
          customer: customerData,
        };
        updateCall(updated);
        antNotification.success({
          message: 'Customer Found',
          description: `Customer: ${customerData.name}`,
          duration: 3,
          placement: 'topRight',
        });
      }
    } catch (err) {
      console.error('[VoipWidget] Manual search error:', err);
    } finally {
      setIsSearching(false);
    }
  }, [updateCall]);

  // ─── Call duration timer ──────────────────────────────────────────────────
  const startCallTimer = useCallback(() => {
    if (callTimerRef.current) window.clearInterval(callTimerRef.current);
    setCallDuration(0);
    callTimerRef.current = window.setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, []);

  const stopCallTimer = useCallback(() => {
    if (callTimerRef.current) {
      window.clearInterval(callTimerRef.current);
      callTimerRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    return () => stopCallTimer();
  }, [stopCallTimer]);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ─── Listen for events dispatched by VoipDialerListener ──────────────────
  useEffect(() => {
    const handleIncomingCall = (event: Event) => {
      const { name, clientId, phone, customer, timestamp } = (event as CustomEvent).detail;

      console.log('[VoipWidget] Event received:', { name, phone, hasCustomer: !!customer });
      console.log('[VoipWidget] currentCallRef.current:', currentCallRef.current);

      const prev = currentCallRef.current;

      if (customer) {
        // ── Second event: customer data arrived — update the existing call ──
        if (prev) {
          console.log('[VoipWidget] Merging customer into existing call');
          const updated: CallRecord = {
            ...prev,
            name:     name     || prev.name,
            clientId: clientId || prev.clientId,
            customer,
          };
          updateCall(updated);
        } else {
          // Edge case: customer arrived but no call record yet — create one
          console.log('[VoipWidget] No prev call, creating new with customer');
          const newCall: CallRecord = {
            id:        String(timestamp || Date.now()),
            name:      name || phone || 'Unknown',
            clientId:  String(clientId || ''),
            phone:     phone || '',
            customer,
            timestamp: new Date(),
          };
          updateCall(newCall);
        }
      } else {
        // ── First event: new incoming call, no customer yet ──
        console.log('[VoipWidget] New incoming call');
        const newCall: CallRecord = {
          id:        String(timestamp || Date.now()),
          name:      name || phone || 'Unknown',
          clientId:  String(clientId || ''),
          phone:     phone || '',
          customer:  null,
          timestamp: new Date(),
        };
        updateCall(newCall);

        setUnreadCount(c => c + 1);
        setIsRinging(true);
        setCallStatus('ringing');
        setCallDuration(0);
        stopCallTimer();
        setDrawerVisible(true);
        if (phone) setSearchKeyword(phone);

        antNotification.info({
          message: '📞 Incoming Call',
          description: `From: ${name || phone}`,
          duration: 5,
          placement: 'topRight',
          onClick: () => setDrawerVisible(true),
        });
      }
    };

    const handleVoipCustomerCreated = (event: Event) => {
      const customer = (event as CustomEvent).detail?.customer;
      const current = currentCallRef.current;
      if (!customer || !current) return;

      // Apply update if this call had no customer yet, or if phone matches.
      const samePhone =
        !!customer.phone &&
        !!current.phone &&
        (customer.phone === current.phone || customer.mobile === current.phone);

      if (!samePhone && current.customer) return;

      const updated: CallRecord = {
        ...current,
        name: customer.name || current.name,
        clientId: String(customer.id ?? current.clientId),
        phone: customer.phone || customer.mobile || current.phone,
        customer: {
          ...(current.customer || {}),
          ...customer,
        },
      };

      updateCall(updated);
    };

    const handleCallStarted = () => {
      setCallStatus('ringing');
      setIsRinging(true);
    };

    const handleCallAnswered = () => {
      setCallStatus('active');
      setIsRinging(false);
      startCallTimer();
    };

    const handleCallEnded = (event: Event) => {
      const { phone, clientId } = (event as CustomEvent).detail || {};
      const current = currentCallRef.current;

      setCallStatus('ended');
      setIsRinging(false);
      stopCallTimer();
      setCallDuration(0);
      setUnreadCount(0);

      if (!current) return;

      const samePhone =
        !!phone && !!current.phone && String(phone) === String(current.phone);
      const sameClient =
        !!clientId &&
        !!current.clientId &&
        String(clientId) === String(current.clientId);

      if (!phone && !clientId) {
        // keep data visible for review after call ends
        return;
      }

      if (!samePhone && !sameClient) return;
      // keep data visible — user can click "Clear" to dismiss
    };

    window.addEventListener('voip-incoming-call', handleIncomingCall);
    window.addEventListener('voip-customer-created', handleVoipCustomerCreated);
    window.addEventListener('voip-call_started', handleCallStarted);
    window.addEventListener('voip-call_answered', handleCallAnswered);
    window.addEventListener('voip-call_ended', handleCallEnded);
    return () => {
      window.removeEventListener('voip-incoming-call', handleIncomingCall);
      window.removeEventListener('voip-customer-created', handleVoipCustomerCreated);
      window.removeEventListener('voip-call_started', handleCallStarted);
      window.removeEventListener('voip-call_answered', handleCallAnswered);
      window.removeEventListener('voip-call_ended', handleCallEnded);
    };
  }, [startCallTimer, stopCallTimer]);

  // Restore last call after refresh (with TTL) and revalidate customer data.
  useEffect(() => {
    const restoreSnapshot = async () => {
      try {
        const raw = localStorage.getItem(VOIP_SNAPSHOT_KEY);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        const savedAt = Number(parsed?.savedAt || 0);
        if (!savedAt || Date.now() - savedAt > VOIP_SNAPSHOT_TTL_MS) {
          clearSnapshot();
          return;
        }

        const snapshotVoipId = String(parsed?.voipId ?? '').trim();
        if (!snapshotVoipId || snapshotVoipId !== currentUserVoipId) {
          clearSnapshot();
          return;
        }

        const snapshotCall = parsed?.currentCall;
        if (!snapshotCall) {
          clearSnapshot();
          return;
        }

        const restoredCall: CallRecord = {
          ...snapshotCall,
          timestamp: new Date(snapshotCall.timestamp || Date.now()),
        };

        updateCall(restoredCall);
        setDrawerVisible(Boolean(parsed?.drawerVisible));
        setUnreadCount(Number(parsed?.unreadCount || 0));
        setSearchKeyword(parsed?.searchKeyword || restoredCall.phone || '');
        setIsRinging(false);

        const id = String(restoredCall.clientId || '');
        if (!id || id === '0') return;

        const detailRes = await Api.get(`customers/${id}/view`);
        const payload = detailRes?.data ?? detailRes;
        const customerData = payload?.customer
          ? {
              ...payload.customer,
              orders: payload.orders ?? [],
              addresses: payload.addresses ?? [],
              activities: payload.activities ?? [],
              most_ordered_products: payload.most_ordered_products ?? [],
              basket_data: payload.basket_data ?? null,
            }
          : payload?.id
          ? { orders: [], addresses: [], ...payload }
          : null;

        if (!customerData) return;

        const current = currentCallRef.current;
        if (!current) return;

        updateCall({
          ...current,
          name: customerData.name || current.name,
          clientId: String(customerData.id ?? current.clientId),
          phone: customerData.phone || customerData.mobile || current.phone,
          customer: customerData,
        });
      } catch (error) {
        console.error('[VoipWidget] Failed to restore snapshot:', error);
        clearSnapshot();
      }
    };

    restoreSnapshot();
  }, [updateCall, clearSnapshot, currentUserVoipId]);

  // Persist active call snapshot for page refresh recovery.
  useEffect(() => {
    if (!currentCall) {
      clearSnapshot();
      return;
    }

    const snapshot = {
      savedAt: Date.now(),
      voipId: currentUserVoipId,
      drawerVisible,
      unreadCount,
      searchKeyword,
      currentCall: {
        ...currentCall,
        timestamp:
          currentCall.timestamp instanceof Date
            ? currentCall.timestamp.toISOString()
            : currentCall.timestamp,
      },
    };

    localStorage.setItem(VOIP_SNAPSHOT_KEY, JSON.stringify(snapshot));
  }, [currentCall, drawerVisible, unreadCount, searchKeyword, clearSnapshot, currentUserVoipId]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const handleCloseDrawer = () => { setDrawerVisible(false); setIsRinging(false); };
  const handleClearCall   = () => {
    updateCall(null);
    clearSnapshot();
    stopCallTimer();
    setCallStatus('idle');
    setCallDuration(0);
    setUnreadCount(0);
    setDrawerVisible(false);
    setIsRinging(false);
    setSearchKeyword('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const handleAddOrder = (clientId: string) => {
    history.push(
      clientId && clientId !== '' && clientId !== '0'
        ? `/orders/add?client_id=${clientId}`
        : '/orders/add'
    );
    handleCloseDrawer();
  };

  const handleCreateNewCustomer = () => {
    // Pass one-time flag + call identity so Add Order can prefill new customer form.
    const params = new URLSearchParams();
    params.set('open_add_customer', String(Date.now()));

    const fallbackName = currentCallRef.current?.name?.trim();
    const fallbackPhone = currentCallRef.current?.phone?.trim();

    if (fallbackName) params.set('prefill_customer_name', fallbackName);
    if (fallbackPhone) params.set('prefill_customer_phone', fallbackPhone);

    history.push(`/orders/add?${params.toString()}`);
    handleCloseDrawer();
  };

  const handleViewOrder = (orderId: number) => {
    history.push(`/orders/show/${orderId}`);
    handleCloseDrawer();
  };

  const handleShowAllOrders = () => {
    const customerName = currentCallRef.current?.customer?.name ?? 
                         currentCallRef.current?.name ?? 
                         '';
    
    handleCloseDrawer();
    
    // Dispatch an event that the orders list can listen to
    window.dispatchEvent(new CustomEvent('set-orders-search', { 
      detail: { search: customerName } 
    }));
    
    history.push('/orders/list');
  };

  const isValidClientId = (id: string) => {
    if (!id || id === '0') return false;
    const n = parseInt(id, 10);
    return !isNaN(n) && n > 0;
  };

  const formatTime = (date: Date) => {
    const diff    = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours   = Math.floor(diff / 3600000);
    const days    = Math.floor(diff / 86400000);
    if (minutes < 1)  return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24)   return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };

  const lastOrder = (() => {
    const orders = currentCall?.customer?.orders;
    if (!orders?.length) return null;
    return [...orders].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  })();

  const totalOrdersCount = getTotalOrdersCount();
  const { averageBasketSize, basketCount, basketTotal, completedBuckets } = getBasketData();

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div
        ref={voipRef}
        className={`voip-widget${isDragging ? ' is-dragging' : ''}`}
        style={position ? { left: position.x, top: position.y, bottom: 'auto', right: 'auto' } : undefined}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <Badge count={unreadCount} offset={[-5, 5]}>
          <div
            className={`voip-widget-button ${callStatus === 'ringing' ? 'ringing-active' : ''}`}
            style={
              callStatus === 'ringing'
                ? undefined
                : callStatus === 'active'
                ? { background: 'linear-gradient(135deg, #52c41a, #389e0d)' }
                : callStatus === 'ended'
                ? { background: 'linear-gradient(135deg, #ff4d4f, #cf1322)' }
                : { background: `linear-gradient(135deg, ${segmentColor}, ${segmentColor})` }
            }
            onClick={handleButtonClick}
          >
            <PhoneOutlined style={{ fontSize: 24, color: '#fff' }} />
            {callStatus === 'ringing' && <div className="ringing-wave" />}
            {callStatus === 'active' && (
              <span style={{ position: 'absolute', bottom: -8, fontSize: 10, color: '#fff', fontWeight: 'bold' }}>
                {formatDuration(callDuration)}
              </span>
            )}
          </div>
        </Badge>
      </div>

      <Drawer
        title={
          <div className="drawer-header">
            <Space>
              <div className={`ringing-icon ${callStatus === 'ringing' ? 'ringing' : ''}`}>
                <PhoneOutlined style={{
                  color: callStatus === 'ended' ? 'light-dark(#ff4d4f, #f25f60)' : callStatus === 'active' ? '#52c41a' : '#52c41a',
                  fontSize: 20,
                }} />
              </div>
              <span>{currentCall ? 'Call Details' : 'VOIP Dialer'}</span>
              {currentCall && callStatus === 'ringing' && (
                <Tag color="processing" icon={<PhoneOutlined />}>Connecting...</Tag>
              )}
              {currentCall && callStatus === 'active' && (
                <Tag color="green" icon={<PhoneOutlined />}>
                  In Call {formatDuration(callDuration)}
                </Tag>
              )}
              {currentCall && callStatus === 'ended' && (
                <Tag color="red" icon={<PhoneOutlined />}>
                  Call Ended {callDuration > 0 ? formatDuration(callDuration) : ''}
                </Tag>
              )}
            </Space>
          </div>
        }
        placement="right"
        onClose={handleCloseDrawer}
        open={drawerVisible}
        width={500}
        className="voip-drawer"
        closable
      >
        {currentCall ? (
          <div className="call-details">
            {/* ── Call header ── */}
            <div className={`call-header ${callStatus === 'ringing' ? 'ringing-animation' : ''}`}>
              <Avatar
                size={80}
                icon={<CustomerServiceOutlined />}
                style={{
                  backgroundColor:
                    callStatus === 'ended' ? '#ff4d4f'
                    : callStatus === 'active' ? '#52c41a'
                    : segmentColor,
                  animation: callStatus === 'ringing' ? 'pulse 1s infinite' : 'none',
                }}
              />
              <div className="caller-info">
                <h2 style={{ color: segmentColor }}>{currentCall.name}</h2>
                {currentCall.phone && (
                  <a
                    href={`tel:${currentCall.phone}`}
                    className="phone-number"
                    style={{ color: segmentColor }}
                  >
                    <PhoneOutlined /> {currentCall.phone}
                  </a>
                )}
                <div className="call-time">
                  <ClockCircleOutlined /> {formatTime(currentCall.timestamp)}
                </div>
                {callStatus === 'ringing' && (
                  <div className="call-status">
                    <Badge status="processing" /><span>Ringing... Waiting for answer</span>
                  </div>
                )}
                {callStatus === 'active' && (
                  <div className="call-status" style={{ color: '#52c41a' }}>
                    <Badge status="success" />
                    <span>In Call — {formatDuration(callDuration)}</span>
                  </div>
                )}
                {callStatus === 'ended' && (
                  <div className="call-status" style={{ color: 'light-dark(#ff4d4f, #f25f60)' }}>
                    <Badge status="error" />
                    <span>Call Ended {callDuration > 0 ? `— Duration: ${formatDuration(callDuration)}` : ''}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Tabs ── */}
            <Tabs
              defaultActiveKey="info"
              items={[
                {
                  key: 'info',
                  label: <span><UserOutlined /> Customer Info</span>,
                  children: (
                    <div className="tab-content">
                      {currentCall.customer ? (
                        <>
                          <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Customer Name">
                              <strong>{currentCall.customer.name || 'Unknown'}</strong>
                            </Descriptions.Item>
                            {currentCall.customer.email && (
                              <Descriptions.Item label="Email">
                                {currentCall.customer.email}
                              </Descriptions.Item>
                            )}
                            {currentCall.customer.phone && (
                              <Descriptions.Item label="Phone Number">
                                {currentCall.customer.phone}
                              </Descriptions.Item>
                            )}
                            {currentCall.customer.birthdate && (
                              <Descriptions.Item label="Birth Date">
                                {new Date(currentCall.customer.birthdate).toLocaleDateString()}
                              </Descriptions.Item>
                            )}
                            {isValidClientId(currentCall.clientId) && (
                              <Descriptions.Item label="Customer ID">
                                <Tag color="green">#{currentCall.clientId}</Tag>
                              </Descriptions.Item>
                            )}
                          </Descriptions>

                          {currentCall.customer.addresses?.length > 0 && (
                            <Card
                              size="small"
                              title={<><EnvironmentOutlined /> Address</>}
                              style={{ marginTop: 16 }}
                            >
                              {currentCall.customer.addresses.map((addr: any, i: number) => (
                                <div key={i}>
                                  {addr.address}
                                  {addr.building_number && ` - Building: ${addr.building_number}`}
                                  {addr.floor            && ` - Floor: ${addr.floor}`}
                                  {addr.apartment_number && ` - Apartment: ${addr.apartment_number}`}
                                  {addr.landmark         && ` - Landmark: ${addr.landmark}`}
                                </div>
                              ))}
                            </Card>
                          )}
                        </>
                      ) : (
                        <Card>
                          <div className="empty-state">
                            <UserOutlined style={{ fontSize: 48, color: '#ccc' }} />
                            <p>No customer information available</p>
                            <p className="hint-text">Phone number: {currentCall.phone || 'Not available'}</p>
                            <Button type="primary" onClick={handleCreateNewCustomer}>
                              Add New Customer
                            </Button>
                          </div>
                        </Card>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'total_orders',
                  label: <span><DatabaseOutlined /> Total Orders</span>,
                  children: (
                    <div className="tab-content">
                      <Card>
                        <Row gutter={16}>
                          <Col span={24}>
                            <Statistic
                              title="Total Orders Count"
                              value={totalOrdersCount}
                              prefix={<ShoppingOutlined />}
                              valueStyle={{ color: 'light-dark(#3f8600, #a4f25f)', fontSize: '48px', fontWeight: 'bold' }}
                            />
                          </Col>
                        </Row>
                        {currentCall.customer?.orders?.length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <Descriptions column={1} size="small" bordered>
                              <Descriptions.Item label="First Order">
                                {new Date(Math.min(...currentCall.customer.orders.map((o: any) => new Date(o.created_at).getTime()))).toLocaleDateString()}
                              </Descriptions.Item>
                              <Descriptions.Item label="Last Order">
                                {new Date(Math.max(...currentCall.customer.orders.map((o: any) => new Date(o.created_at).getTime()))).toLocaleDateString()}
                              </Descriptions.Item>
                            </Descriptions>
                          </div>
                        )}
                        {totalOrdersCount === 0 && (
                          <div style={{ marginTop: 16, textAlign: 'center' }}>
                            <Button type="primary" onClick={() => handleAddOrder(currentCall.clientId)}>
                              Create First Order
                            </Button>
                          </div>
                        )}
                      </Card>
                    </div>
                  ),
                },
                {
                  key: 'avg_basket',
                  label: <span><ShoppingCartOutlined /> Average Basket Size</span>,
                  children: (
                    <div className="tab-content">
                      <Card>
                        <Row gutter={16}>
                          <Col span={24}>
                            <Statistic
                              title="Average Basket Size (Completed)"
                              value={averageBasketSize}
                              precision={2}
                              prefix={<ShoppingCartOutlined />}
                              suffix={currency}
                              valueStyle={{ color: 'light-dark(#1890ff, #5fabf2)', fontSize: '40px', fontWeight: 'bold' }}
                            />
                          </Col>
                        </Row>

                        <div style={{ marginTop: 16 }}>
                          <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Completed Buckets">
                              {completedBuckets.map((bucket) => bucket.key).join(', ') || 'None'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Completed Count">
                              {basketCount}
                            </Descriptions.Item>
                            <Descriptions.Item label="Completed Total">
                              {basketTotal.toFixed(2)} {currency}
                            </Descriptions.Item>
                            <Descriptions.Item label="Formula">
                              {basketTotal.toFixed(2)} / {basketCount || 1} = {averageBasketSize.toFixed(2)}
                            </Descriptions.Item>
                          </Descriptions>
                        </div>
                      </Card>
                    </div>
                  ),
                },
                {
                  key: 'last_order',
                  label: <span><HistoryOutlined /> Last Order</span>,
                  children: (
                    <div className="tab-content">
                      {currentCall.customer?.orders?.length > 0 ? (
                        <>
                          {lastOrder && (
                            <Card
                              size="small"
                              title="Last Order"
                              style={{ marginBottom: 16, borderColor: '#52c41a' }}
                            >
                              <Descriptions column={1} size="small">
                                <Descriptions.Item label="Order Number">
                                  <strong>#{lastOrder.id}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Date">
                                  {new Date(lastOrder.created_at).toLocaleDateString()}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                  <Tag color="blue">
                                    {lastOrder.order_status &&
                                    typeof lastOrder.order_status === "object" ? (
                                      <NameByLang item={lastOrder.order_status} />
                                    ) : (
                                      lastOrder.order_status ||
                                      lastOrder.order_status_name ||
                                      'Processing'
                                    )}
                                  </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Amount">
                                  <strong style={{ color: '#52c41a' }}>
                                    {parseFloat(lastOrder.total).toFixed(2)} {currency}
                                  </strong>
                                </Descriptions.Item>
                              </Descriptions>
                              <Button
                                type="link"
                                onClick={() => handleViewOrder(lastOrder.id)}
                                style={{ padding: 0, marginTop: 8 }}
                              >
                                View Order Details
                              </Button>
                            </Card>
                          )}
                        </>
                      ) : (
                        <Card>
                          <div className="empty-state">
                            <HistoryOutlined style={{ fontSize: 48, color: '#ccc' }} />
                            <p>No previous orders</p>
                            <Button type="primary" onClick={() => handleAddOrder(currentCall.clientId)}>
                              Create New Order
                            </Button>
                          </div>
                        </Card>
                      )}
                    </div>
                  ),
                },
              ]}
            />

            <div className="drawer-actions">
              <Button
                type="primary"
                size="large"
                block
                icon={<HistoryOutlined />}
                onClick={handleShowAllOrders}
              >
                View All Orders
              </Button>
            </div>
          </div>
        ) : (
          <div className="empty-call">
            <PhoneOutlined style={{ fontSize: 64, color: '#ccc', marginBottom: 20 }} />
            <h3>No Active Calls</h3>
            <p>Incoming calls will appear here automatically</p>
          </div>
        )}
      </Drawer>
    </>
  );
};